"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ArrowRight, Layers, Pill, ScanLine } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface SolutionItem {
  id: string;
  title: string;
  description: string;
  path: string;
  iconName: "activity" | "layers" | "scan" | "pill";
}
const solutions: SolutionItem[] = [{
  id: "pat",
  title: "Process Analytical Technology",
  description: "Real-time inline and online process monitoring solutions for continuous manufacturing and batch verification.",
  path: "/ProcessAnalyticalTechnologyPage",
  iconName: "activity"
}, {
  id: "chromatography",
  title: "Chromatography",
  description: "Sustainable solvent recovery systems, columns, and method-supporting tools for analytical separations.",
  path: "/ChromatographyPage",
  iconName: "layers"
}, {
  id: "raw-material-id",
  title: "Raw Material Identification",
  description: "Handheld and benchtop spectroscopy systems providing instant chemical ID verification at receipt.",
  path: "/RawMaterialIdentificationPage",
  iconName: "scan"
}, {
  id: "tablet-testing",
  title: "Tablet Testing",
  description: "Physical testing instruments for tablet hardness, disintegration, dissolution, and automated friability.",
  path: "/TabletTestingPage",
  iconName: "pill"
}];
function renderSolutionIcon(iconName: SolutionItem["iconName"]) {
  switch (iconName) {
    case "activity":
      return <Activity className="size-6 text-primary" aria-hidden="true" />;
    case "layers":
      return <Layers className="size-6 text-primary" aria-hidden="true" />;
    case "scan":
      return <ScanLine className="size-6 text-primary" aria-hidden="true" />;
    case "pill":
      return <Pill className="size-6 text-primary" aria-hidden="true" />;
    default:
      return <Activity className="size-6 text-primary" aria-hidden="true" />;
  }
}
export default function RelatedSolutions() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="relatedsolutionsgrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-wider uppercase text-primary mb-2 block">
              Application Hubs
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Explore Related Analytical Solutions
            </h2>
            <p className="mt-3 text-base text-muted-foreground">
              Find dedicated instrumentation, qualification protocols, and lifecycle support tailored to your regulated laboratory workflows.
            </p>
          </div>
          <div>
            <Button asChild variant="outline" className="group">
              <Link to="/SolutionsPage">
                <span>View All Solutions</span>
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {solutions.map((solution, index) => <motion.div key={solution.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} className="h-full flex flex-col">
              <Card className={`h-full flex flex-col justify-between transition-all duration-200 hover:shadow-md hover:border-primary/40 border border-border ${index % 2 === 0 ? "bg-card" : "bg-secondary/40"}`}>
                <CardHeader className="space-y-4">
                  <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    {renderSolutionIcon(solution.iconName)}
                  </div>
                  <CardTitle className="text-xl font-semibold leading-snug text-card-foreground">
                    {solution.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {solution.description}
                  </CardDescription>
                </CardContent>
                <CardFooter className="pt-2">
                  <Button asChild variant="ghost" size="sm" className="p-0 h-auto font-semibold text-primary hover:text-primary/80 hover:bg-transparent group">
                    <Link to={solution.path} className="inline-flex items-center gap-1.5">
                      <span>Explore Solution</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}